import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Button, Chip } from '@mui/material';
import {
  TrendingUp,
  AccessTime,
  Bolt,
  Notes as NotesIcon,
  Call as CallIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Person as PersonIcon,
  LocalFireDepartment,
  ChevronRight,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import crmService, { CrmCommandCenter, CrmPriority, CrmFeedItem, PrioritySeverity } from '../../services/crmService';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import { ErrorState } from '../../components/common/pageKit';

// ---- pure formatters (no theme dependency) ----
const isNum = (v: any) => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Number(v).toFixed(1)}%` : '—');
const fmtDays = (v: any) => (isNum(v) ? `${Number(v).toFixed(0)}d` : '—');
const fmtRatio = (v: any) => (isNum(v) ? `${Number(v).toFixed(2)}x` : '—');

const timeAgo = (ts: string): string => {
  const t = new Date(ts).getTime();
  if (isNaN(t)) return ts;
  const diffMin = Math.floor((Date.now() - t) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(ts).toLocaleDateString('en-IN');
};

const kindIcon = (kind: string) => {
  const k = (kind || '').toLowerCase();
  if (k.includes('call')) return CallIcon;
  if (k.includes('email') || k.includes('mail')) return EmailIcon;
  if (k.includes('meet') || k.includes('demo') || k.includes('event')) return EventIcon;
  if (k.includes('note')) return NotesIcon;
  if (k.includes('lead')) return PersonIcon;
  if (k.includes('won') || k.includes('deal') || k.includes('opp')) return TrendingUp;
  return Bolt;
};

const SEVERITY_ORDER: Record<PrioritySeverity, number> = { high: 0, medium: 1, low: 2 };

const RevenueCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const T = useChartTokens();

  const [data, setData] = useState<CrmCommandCenter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    crmService
      .getCommandCenter()
      .then((res) => setData(res))
      .catch((e: any) => setError(e?.message || 'Failed to load the revenue command center.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = data?.kpis || {};
  const forecast = data?.forecast;
  const priorities = data?.myPriorities || [];
  const feed = data?.activityFeed || [];
  const hotLeads = data?.hotLeads || [];

  const hasKpi = (key: string) => Object.prototype.hasOwnProperty.call(kpis, key);

  const kpiDefs = useMemo(
    () => [
      { key: 'revenue', label: 'Revenue', fmt: fmtMoney, color: C.good },
      { key: 'winRatePct', label: 'Win Rate', fmt: fmtPct, color: C.accent },
      { key: 'avgDealSize', label: 'Avg Deal Size', fmt: fmtMoney, color: C.cyan },
      { key: 'avgSalesCycleDays', label: 'Avg Sales Cycle', fmt: fmtDays, color: C.purple },
      { key: 'quotaAttainmentPct', label: 'Quota Attainment', fmt: fmtPct, color: C.warn },
      { key: 'pipelineCoverage', label: 'Pipeline Coverage', fmt: fmtRatio, color: C.cyan },
      { key: 'grossMarginPct', label: 'Gross Margin', fmt: fmtPct, color: C.good },
    ],
    [C]
  );

  const visibleKpis = kpiDefs.filter((k) => hasKpi(k.key));

  const forecastData = useMemo(
    () =>
      forecast
        ? [
            { name: 'Commit', value: Number(forecast.commit) || 0 },
            { name: 'Best Case', value: Number(forecast.bestCase) || 0 },
            { name: 'Pipeline', value: Number(forecast.pipeline) || 0 },
            { name: 'Weighted', value: Number(forecast.weightedPipeline) || 0 },
            { name: 'Won', value: Number(forecast.won) || 0 },
          ]
        : [],
    [forecast]
  );

  const sortedPriorities = useMemo(
    () => [...priorities].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3)),
    [priorities]
  );

  const severityColor = (s: PrioritySeverity) => (s === 'high' ? C.bad : s === 'medium' ? C.warn : C.sub);

  const goto = (link?: string | null, fallback?: string) => {
    if (link) navigate(link);
    else if (fallback) navigate(fallback);
  };

  const gradeChip = (grade?: string) => {
    const g = (grade || '').toUpperCase();
    if (g === 'HOT') return { label: 'HOT', color: C.bad };
    if (g === 'WARM') return { label: 'WARM', color: C.warn };
    if (g) return { label: g, color: C.sub };
    return { label: '—', color: C.sub };
  };

  const Metric: React.FC<{ label: string; value: React.ReactNode; color?: string; onClick?: () => void }> = ({
    label,
    value,
    color,
    onClick,
  }) => (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: C.panel,
        border: `1px solid ${C.line}`,
        borderRadius: 2,
        p: 2,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
        '&:hover': onClick ? { borderColor: C.accent } : undefined,
      }}
    >
      <Typography
        sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}
      </Typography>
      <Typography
        title={typeof value === 'string' ? value : undefined}
        sx={{
          fontWeight: 800,
          fontSize: { xs: 17, sm: 19, md: 20, lg: 22 },
          lineHeight: 1.15,
          color: color || C.text,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, color: C.text }}>
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${C.line}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          background: `linear-gradient(90deg, ${C.panel}, ${C.bg})`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TrendingUp sx={{ color: C.good }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Revenue Command Center</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>
              What should the team do today, and what will close?
            </Typography>
          </Box>
          {data?.period?.fy && (
            <Chip
              size="small"
              label={`FY ${data.period.fy}`}
              sx={{ bgcolor: 'transparent', color: C.sub, border: `1px solid ${C.line}`, fontSize: 11 }}
            />
          )}
        </Stack>
        <Button variant="outlined" onClick={() => navigate('/sales/opportunities')} sx={{ color: C.text, borderColor: C.line }}>
          All Opportunities
        </Button>
      </Box>

      {loading && <LinearProgress />}

      {error && !data ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          {/* Exec KPI band */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              display: 'grid',
              // auto-fit so a tile is never squeezed below a legible width — the
              // fixed 7-column layout cut off compact ₹ figures on md screens.
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(auto-fit, minmax(150px, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {visibleKpis.length ? (
              visibleKpis.map((k) => (
                <Metric key={k.key} label={k.label} value={k.fmt(kpis[k.key])} color={k.color} />
              ))
            ) : (
              !loading && <Typography sx={{ color: C.sub, gridColumn: '1 / -1' }}>No KPI data available.</Typography>
            )}
          </Box>

          {/* Forecast panel */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              Forecast · click to open pipeline
            </Typography>
            <Box
              sx={{
                bgcolor: C.panel,
                border: `1px solid ${C.line}`,
                borderRadius: 2,
                p: 2,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                gap: 2,
              }}
            >
              <Box sx={{ width: '100%', height: 260 }}>
                {forecastData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={forecastData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid {...gridProps(T)} />
                      <XAxis dataKey="name" {...axisProps(T)} />
                      <YAxis {...axisProps(T)} tickFormatter={(v: number) => formatIndianNumber(v)} width={70} />
                      <Tooltip {...tooltipStyle(T)} formatter={(v: any) => formatIndianNumber(Number(v))} />
                      {forecast?.target != null && (
                        <ReferenceLine
                          y={Number(forecast.target)}
                          stroke={T.status.warning}
                          strokeDasharray="4 4"
                          label={{ value: 'Target', position: 'right', fill: T.textSecondary, fontSize: 11 }}
                        />
                      )}
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} onClick={() => navigate('/sales/opportunities')} cursor="pointer">
                        {forecastData.map((_, i) => (
                          <Cell key={i} fill={T.series(i)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography sx={{ color: C.sub, p: 2 }}>Forecast data unavailable.</Typography>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3,1fr)', md: '1fr' }, gap: 1.5 }}>
                <Metric label="Target" value={fmtMoney(forecast?.target)} onClick={() => navigate('/sales/opportunities')} />
                <Metric
                  label="Coverage"
                  value={forecast?.coveragePct != null ? `${Number(forecast.coveragePct).toFixed(0)}%` : '—'}
                  color={
                    forecast?.coveragePct != null ? (Number(forecast.coveragePct) < 100 ? C.bad : C.good) : undefined
                  }
                  onClick={() => navigate('/sales/opportunities')}
                />
                <Metric
                  label="Gap"
                  value={fmtMoney(forecast?.gap)}
                  color={forecast?.gap != null ? (Number(forecast.gap) > 0 ? C.bad : C.good) : undefined}
                  onClick={() => navigate('/sales/opportunities')}
                />
              </Box>
            </Box>
          </Box>

          {/* Priorities + Feed */}
          <Box sx={{ px: 3, pb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2 }}>
            {/* My Priorities */}
            <Box>
              <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                My Priorities · what to do today
              </Typography>
              <Stack spacing={1}>
                {sortedPriorities.map((p: CrmPriority, i: number) => (
                  <Box
                    key={i}
                    sx={{
                      bgcolor: C.panel,
                      border: `1px solid ${C.line}`,
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: severityColor(p.severity), flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>
                        {p.label}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: C.sub, textTransform: 'capitalize' }}>{p.type}</Typography>
                    </Box>
                    <Button
                      size="small"
                      endIcon={<ChevronRight />}
                      disabled={!p.link}
                      onClick={() => goto(p.link)}
                      sx={{ color: C.accent, flexShrink: 0 }}
                    >
                      Open
                    </Button>
                  </Box>
                ))}
                {!loading && !sortedPriorities.length && (
                  <Typography sx={{ color: C.sub, fontStyle: 'italic', py: 2 }}>Nothing needs attention.</Typography>
                )}
              </Stack>
            </Box>

            {/* Activity Feed */}
            <Box>
              <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                Activity Feed
              </Typography>
              <Stack spacing={1} sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}>
                {feed.map((f: CrmFeedItem, i: number) => {
                  const Icon = kindIcon(f.kind);
                  return (
                    <Box
                      key={i}
                      onClick={() => goto(f.link)}
                      sx={{
                        bgcolor: C.panel,
                        border: `1px solid ${C.line}`,
                        borderRadius: 2,
                        p: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        cursor: f.link ? 'pointer' : 'default',
                        '&:hover': f.link ? { borderColor: C.accent } : undefined,
                      }}
                    >
                      <Icon sx={{ fontSize: 16, color: C.cyan, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 12.5, flex: 1, minWidth: 0 }} noWrap>
                        {f.label}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <AccessTime sx={{ fontSize: 12, color: C.sub }} />
                        <Typography sx={{ fontSize: 10.5, color: C.sub, whiteSpace: 'nowrap' }}>{timeAgo(f.ts)}</Typography>
                      </Stack>
                    </Box>
                  );
                })}
                {!loading && !feed.length && (
                  <Typography sx={{ color: C.sub, fontStyle: 'italic', py: 2 }}>No recent activity.</Typography>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Hot Leads */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              Hot Leads
            </Typography>
            {hotLeads.length ? (
              <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                {hotLeads.map((lead: Record<string, any>, i: number) => {
                  const chip = gradeChip(lead.lead_grade);
                  return (
                    <Box
                      key={lead.id ?? i}
                      onClick={() => navigate('/crm/leads')}
                      sx={{
                        cursor: 'pointer',
                        minWidth: 220,
                        flexShrink: 0,
                        bgcolor: C.panel,
                        border: `1px solid ${C.line}`,
                        borderRadius: 2,
                        p: 1.5,
                        '&:hover': { borderColor: C.accent },
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }} noWrap>
                          {lead.company_name || '—'}
                        </Typography>
                        <Chip
                          size="small"
                          icon={chip.label === 'HOT' ? <LocalFireDepartment sx={{ fontSize: 13 }} /> : undefined}
                          label={chip.label}
                          sx={{ bgcolor: 'transparent', color: chip.color, border: `1px solid ${chip.color}`, fontSize: 10, height: 20 }}
                        />
                      </Stack>
                      <Typography sx={{ fontSize: 11.5, color: C.sub }} noWrap>
                        {lead.contact_person || '—'}
                      </Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                        <Typography sx={{ fontSize: 11, color: C.sub }}>Score {lead.lead_score ?? '—'}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.good }}>
                          {isNum(lead.estimated_value) ? formatIndianNumber(Number(lead.estimated_value)) : '—'}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              !loading && <Typography sx={{ color: C.sub, fontStyle: 'italic' }}>No hot leads right now.</Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default RevenueCommandCenter;
